import {
  ActionError,
  AttentionBlock,
  Button,
  ConfirmDialog,
  Field,
  FormBlock,
  FormFooter,
  PageHeader,
  PageTitle,
  ReadingColumn,
  SavedIndicator,
  StatusTag,
  Surface,
  Textarea,
  formatDate,
  plural,
  titleCase,
  toast,
} from '@westhetics/ui'
import { useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import {
  AddressBlock,
  ContactBlock,
  FormSkeleton,
  IdentificationBlock,
  TextField,
} from '@/features/clinic-profile/blocks'
import {
  DOC_TYPE_LABELS,
  docTypeOf,
  formatDocNumber,
  normalizeDocNumber,
} from '@/features/clinic-profile/doc-number'
import {
  ADDRESS_KEYS,
  CONTACT_KEYS,
  DOC_NUMBER_KEYS,
  EMPTY_CLINIC_FORM,
  IDENTIFICATION_KEYS,
  clinicFieldKey,
  clinicFormFrom,
  companyInput,
  placeFieldErrors,
  validateClinicForm,
  type ClinicFormState,
} from '@/features/clinic-profile/form'
import { focusFirstInvalid, useFormState } from '@/features/clinic-profile/use-form-state'
import { STEP_LABELS } from '@/features/onboarding/api'
import { stepStatesFromSummary, type StepState } from '@/features/onboarding/steps'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import {
  useBlockClinic,
  useClinic,
  useUnblockClinic,
  useUpdateClinic,
  type AdminClinic,
} from './api'
import { AudiencesBlock } from './audiences-block'
import { clinicName, clinicStatus, onboardingSummary } from './labels'
import { NetworkBlock } from './network-block'

/**
 * Detalhe da clínica no painel root (docs/areas/administracao.md, "Detalhe da clínica";
 * RN-ADM-01, 51, 52, 54 e 85). Arquétipo: cadastro, com o estado da implantação no topo e o
 * bloqueio num cartão à parte. Testemunhas, responsável legal e assinatura são da gestora: root
 * entra na clínica e usa Empresa. A rede só sai ou entra por aqui, nunca troca direto (P-ADM-83).
 */

/** O cadastro de Empresa mais a rede, que só root edita (RN-ADM-52). */
interface AdminClinicFormState extends ClinicFormState {
  /** O id da rede escolhida; vazio é "Sem rede". */
  networkId: string
}

const KEYS = [...DOC_NUMBER_KEYS, ...IDENTIFICATION_KEYS, ...ADDRESS_KEYS, ...CONTACT_KEYS]

function adminClinicFormFrom(clinic: AdminClinic): AdminClinicFormState {
  return { ...clinicFormFrom(clinic), networkId: clinic.network?.id ?? '' }
}

/** O campo do formulário para um caminho do 422: a rede, ou o que Empresa já resolve. */
function adminClinicFieldKey(path: string, values: ClinicFormState) {
  return path === 'networkId' ? 'networkId' : clinicFieldKey(path, values)
}

const MSG_JUSTIFICATION = 'Escreva o motivo em 10 a 500 caracteres.'

const isJustification = (text: string) => text.trim().length >= 10 && text.trim().length <= 500

export function ClinicPage() {
  const { id } = useParams()
  const query = useClinic(id)
  // O formulário nasce da clínica carregada; outro id, outro formulário.
  return <ClinicForm key={query.data?.id ?? 'loading'} id={id ?? ''} query={query} />
}

function ClinicForm({ id, query }: { id: string; query: ReturnType<typeof useClinic> }) {
  const navigate = useNavigate()
  const session = useSession()
  const clinic = query.data
  const save = useUpdateClinic(id)
  const block = useBlockClinic()
  const unblock = useUnblockClinic()
  const form = useFormState<AdminClinicFormState>(
    () => (clinic ? adminClinicFormFrom(clinic) : { ...EMPTY_CLINIC_FORM, networkId: '' }),
    validateClinicForm,
  )
  const formRef = useRef<HTMLFormElement>(null)

  const [entering, setEntering] = useState(false)
  const [enterError, setEnterError] = useState<unknown>(null)
  const [justification, setJustification] = useState('')
  const [justificationTouched, setJustificationTouched] = useState(false)
  const [confirmingBlock, setConfirmingBlock] = useState(false)

  const name = clinic ? clinicName(clinic) : 'Clínica'
  const isBlocked = clinic?.status === 'blocked'

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!clinic) return
    if (!form.check(KEYS)) {
      focusFirstInvalid(formRef.current)
      return
    }
    const values = form.values
    const docNumber = normalizeDocNumber(values.docNumber)
    const savedNetworkId = clinic.network?.id ?? ''
    save.mutate(
      {
        ...companyInput(values),
        audiences: values.audiences,
        // O documento só vai quando mudou: depois do primeiro atendimento ele é imutável (RN-ADM-01).
        ...(docNumber !== clinic.docNumber ? { docNumber, docType: docTypeOf(docNumber) } : {}),
        // A rede só vai quando mudou; vazio é "Sem rede" (RN-ADM-54, P-ADM-83).
        ...(values.networkId !== savedNetworkId ? { networkId: values.networkId || null } : {}),
      },
      {
        onSuccess: (saved) => {
          form.markSaved()
          toast(`Dados de ${clinicName(saved)} salvos — passam a valer nos próximos documentos`)
        },
        onError: (error) => {
          form.refuse(placeFieldErrors(error, (path) => adminClinicFieldKey(path, values)).fields)
          focusFirstInvalid(formRef.current)
        },
      },
    )
  }

  /** Root passa a agir como gestora desta clínica (RN-ADM-85) e segue para a implantação. */
  async function enter() {
    if (!clinic) return
    setEntering(true)
    setEnterError(null)
    try {
      await session.switchClinic(clinic.id)
      navigate(clinic.onboarding.isCompleted ? routes.dashboard : routes.onboarding)
    } catch (error) {
      setEnterError(error)
      setEntering(false)
    }
  }

  function askBlock() {
    setJustificationTouched(true)
    if (isJustification(justification)) setConfirmingBlock(true)
  }

  function confirmBlock() {
    if (!clinic) return
    block.mutate(
      { id: clinic.id, justification: justification.trim() },
      {
        onSuccess: () => {
          setConfirmingBlock(false)
          setJustification('')
          setJustificationTouched(false)
          toast(`"${name}" bloqueada — o acesso está suspenso`)
        },
        onError: () => setConfirmingBlock(false),
      },
    )
  }

  function confirmUnblock() {
    if (!clinic) return
    setJustificationTouched(true)
    const text = justification.trim()
    if (text !== '' && !isJustification(text)) return
    unblock.mutate(
      { id: clinic.id, justification: text || undefined },
      {
        onSuccess: () => {
          setJustification('')
          setJustificationTouched(false)
          toast(`"${name}" desbloqueada — o acesso voltou como estava`)
        },
      },
    )
  }

  const failure = save.isError
    ? placeFieldErrors(save.error, (path) => adminClinicFieldKey(path, form.values))
    : null
  const showFailure =
    failure !== null && (Object.keys(failure.fields).length === 0 || failure.unplaced.length > 0)
  const existing =
    save.error && isApiError(save.error) && save.error.code === 'conflict'
      ? (save.error.details.existing as { id: string } | undefined)
      : undefined
  const accessError = block.error ?? unblock.error
  const justificationError = !justificationTouched
    ? undefined
    : isBlocked
      ? justification.trim() !== '' && !isJustification(justification)
        ? MSG_JUSTIFICATION
        : undefined
      : isJustification(justification)
        ? undefined
        : MSG_JUSTIFICATION

  return (
    <form ref={formRef} onSubmit={submit} noValidate>
      <PageHeader
        LinkComponent={Link}
        crumbs={[{ label: 'Clínicas', href: routes.admin.clinics }, { label: clinic ? name : '…' }]}
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to={routes.admin.clinics}>Voltar</Link>
          </Button>
        }
      />
      <Page>
        <ReadingColumn wide>
          {query.isError ? (
            <>
              <PageTitle title="Clínica" />
              <LoadError
                className="mt-6"
                title="Não conseguimos abrir esta clínica"
                error={query.error}
                onRetry={() => void query.refetch()}
              />
            </>
          ) : !clinic ? (
            <>
              <PageTitle title="Clínica" />
              <FormSkeleton blocks={4} />
            </>
          ) : (
            <>
              <PageTitle
                title={name}
                meta={identificationLine(clinic)}
                trailing={
                  <StatusTag level={clinicStatus(clinic).level}>
                    {clinicStatus(clinic).label}
                  </StatusTag>
                }
              />

              <div className="mt-6 flex flex-col gap-4">
                {isBlocked ? (
                  <AttentionBlock title="O acesso desta clínica está suspenso">
                    <p>
                      {blockedLine(clinic)} Ninguém da clínica entra e os links dos pacientes não
                      abrem. Nada foi apagado.
                    </p>
                    {clinic.blockJustification ? (
                      <p>Justificativa: {clinic.blockJustification}</p>
                    ) : null}
                  </AttentionBlock>
                ) : null}

                {showFailure ? (
                  <ActionError
                    title="Não foi possível salvar a clínica"
                    description={
                      failure.unplaced.length > 0
                        ? failure.unplaced.join(' ')
                        : `${errorMessage(save.error)} O que você digitou continua na tela.`
                    }
                    actions={
                      existing && existing.id !== clinic.id ? (
                        <Button variant="outline" size="sm" asChild>
                          <Link to={routes.admin.clinic(existing.id)}>
                            Abrir a clínica existente
                          </Link>
                        </Button>
                      ) : undefined
                    }
                  />
                ) : null}

                {enterError ? (
                  <ActionError
                    title="Não foi possível entrar na clínica"
                    description={`${errorMessage(enterError)} Você continua na visão da Westhetics.`}
                    onDismiss={() => setEnterError(null)}
                  />
                ) : null}

                <FormBlock
                  title="Implantação"
                  note="As quatro etapas até a clínica emitir o primeiro documento."
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="font-display text-lead text-ink leading-[1.3] font-normal">
                      {clinic.onboarding.isCompleted
                        ? clinic.onboarding.completedAt
                          ? `Concluída em ${formatDate(new Date(clinic.onboarding.completedAt))}`
                          : 'Concluída'
                        : onboardingSummary(clinic.onboarding)}
                    </p>
                    <Button
                      className="max-desk:min-h-touch"
                      variant="outline"
                      loading={entering}
                      loadingLabel="Entrando na clínica…"
                      onClick={() => void enter()}
                    >
                      Entrar na clínica
                    </Button>
                  </div>
                  <ol className="border-hairline mt-3 border-t">
                    {stepStatesFromSummary(clinic.onboarding).map(({ key, state }) => (
                      <li
                        key={key}
                        className="border-hairline font-body text-body flex items-baseline justify-between gap-4 border-b py-[10px] font-light last:border-b-0"
                      >
                        <span className="text-ink">{STEP_LABELS[key]}</span>
                        <span className={state === 'pending' ? 'text-plum' : 'text-secondary'}>
                          {STEP_STATE_LABELS[state]}
                        </span>
                      </li>
                    ))}
                  </ol>
                  <p className="font-body text-eyebrow text-label mt-3 leading-[1.5] font-light tracking-normal">
                    Entrar na clínica troca a sua clínica ativa: você passa a agir como gestora dela
                    e pode fazer a implantação inteira.
                  </p>
                </FormBlock>

                <IdentificationBlock
                  form={form}
                  note="Aparece no cabeçalho e na qualificação das partes de todo documento."
                  document={
                    <TextField
                      form={form}
                      name="docNumber"
                      label="CNPJ ou CPF"
                      autoCapitalize="characters"
                      mask={formatDocNumber}
                      hint="Só pode ser corrigido enquanto a clínica não emitiu nenhum documento."
                    />
                  }
                />
                <AddressBlock form={form} />
                <ContactBlock form={form} />
                <AudiencesBlock
                  value={form.values.audiences}
                  onChange={(audiences) => form.update({ audiences })}
                  error={form.errors.audiences}
                />
                <NetworkBlock
                  value={form.values.networkId}
                  onChange={(networkId) => form.update({ networkId })}
                  current={clinic.network}
                  error={form.errors.networkId}
                />

                <FormFooter
                  status={
                    <SavedIndicator
                      savedAt={new Date(clinic.updatedAt)}
                      dirty={form.dirty}
                      saving={save.isPending}
                    />
                  }
                  secondary={
                    <Button variant="outline" asChild>
                      <Link to={routes.admin.clinics}>Cancelar</Link>
                    </Button>
                  }
                  primary={
                    <Button type="submit" loading={save.isPending} loadingLabel="Salvando…">
                      Salvar
                    </Button>
                  }
                />

                {accessError ? (
                  <ActionError
                    title={
                      isBlocked
                        ? 'Não foi possível desbloquear a clínica'
                        : 'Não foi possível bloquear a clínica'
                    }
                    description={errorMessage(accessError)}
                  />
                ) : null}

                <Surface className="flex flex-col gap-4">
                  <div className="max-w-[560px]">
                    <div className="font-display text-lead text-ink leading-[1.3] font-normal">
                      {isBlocked ? 'Desbloquear clínica' : 'Bloquear clínica'}
                    </div>
                    <p className="font-body text-secondary mt-1 text-[12px] leading-[1.6] font-light">
                      {isBlocked
                        ? 'Desbloquear restaura o acesso de todo mundo da clínica e os links dos pacientes, como estavam.'
                        : 'Bloquear suspende o acesso de todo mundo da clínica e os links dos pacientes. Nada é apagado.'}
                    </p>
                  </div>
                  <Field
                    label="Justificativa"
                    optional={isBlocked}
                    meta={`${justification.trim().length}/500`}
                    error={justificationError}
                    hint="Fica na trilha da clínica, com o seu nome e a data."
                  >
                    <Textarea
                      value={justification}
                      maxLength={500}
                      onChange={(event) => setJustification(event.target.value)}
                      onBlur={() => setJustificationTouched(justification.trim() !== '')}
                    />
                  </Field>
                  <div>
                    {isBlocked ? (
                      <Button
                        className="max-desk:min-h-touch"
                        variant="outline"
                        loading={unblock.isPending}
                        loadingLabel="Desbloqueando…"
                        onClick={confirmUnblock}
                      >
                        Desbloquear clínica
                      </Button>
                    ) : (
                      <Button className="max-desk:min-h-touch" variant="outline" onClick={askBlock}>
                        Bloquear clínica
                      </Button>
                    )}
                  </div>
                </Surface>
              </div>
            </>
          )}
        </ReadingColumn>
      </Page>

      <ConfirmDialog
        open={confirmingBlock}
        onOpenChange={setConfirmingBlock}
        title={`Bloquear "${name}"?`}
        effect={`O acesso de todo mundo da clínica${
          clinic && clinic.counts.users > 0 ? ` (${plural(clinic.counts.users, 'pessoa')})` : ''
        } é suspenso agora, e os links de anamnese, assinatura e fotos dos pacientes deixam de abrir. Nada é apagado: vínculos, documentos e dados continuam como estão, e desbloquear restaura tudo.`}
        confirmLabel="Bloquear"
        loading={block.isPending}
        loadingLabel="Bloqueando…"
        onConfirm={confirmBlock}
      />
    </form>
  )
}

const STEP_STATE_LABELS: Record<StepState, string> = {
  done: 'feita',
  pending: 'falta',
  unknown: 'não informado',
}

/** "CNPJ 11.222.333/0001-81 · São Paulo/SP · criada em 20/03/2026 · 2 gestoras". */
function identificationLine(clinic: AdminClinic) {
  return [
    `${DOC_TYPE_LABELS[clinic.docType]} ${formatDocNumber(clinic.docNumber, clinic.docType)}`,
    clinic.address ? `${clinic.address.city}/${clinic.address.state}` : 'sem endereço',
    `criada em ${formatDate(new Date(clinic.createdAt))}`,
    plural(clinic.counts.managers, 'gestora'),
  ].join(' · ')
}

/** "Bloqueada em 01/09/2026 por Helena Prado." */
function blockedLine(clinic: AdminClinic) {
  const when = clinic.blockedAt ? ` em ${formatDate(new Date(clinic.blockedAt))}` : ''
  const who = clinic.blockedBy ? ` por ${titleCase(clinic.blockedBy.name)}` : ''
  return `Bloqueada${when}${who}.`
}
